import React, { useState } from 'react';
import { toast } from 'sonner';
import { LifeBuoy, Loader2, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { videoApiClient } from '@/services/videoApi';
import { trackEvent } from '@/lib/analytics';

/**
 * "Need help with your creative?" modal. Collects an email (required) + an
 * optional description and sends it to the team via the feedback endpoint
 * (which pings FEEDBACK_CHAT_WEBHOOK_URL). Does not change the creative.
 */
export default function CreativeHelpForm({ open, onOpenChange, creativeId }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleOpenChange = (next) => {
    onOpenChange(next);
    if (!next) {
      setTimeout(() => {
        setEmail('');
        setMessage('');
        setSubmitted(false);
      }, 200);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email so our team can reach you.');
      return;
    }
    setSubmitting(true);
    try {
      await videoApiClient.submitFeedback({
        message: `Creative ${creativeId || '(unknown)'} — ${message.trim() || 'Requested help adapting this creative.'}`,
        category: 'Creative help',
        email: email.trim(),
        page_url: window.location.pathname,
      });
      trackEvent('creative_help_requested', { creative_id: creativeId || null });
      setSubmitted(true);
    } catch (err) {
      toast.error('Could not send your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        {submitted ? (
          <div className="flex flex-col items-center text-center py-8 space-y-3">
            <CheckCircle2 className="h-12 w-12 text-teal" />
            <DialogTitle>Request sent!</DialogTitle>
            <DialogDescription>
              One of our specialists will reach out to you shortly.
            </DialogDescription>
            <Button className="mt-2" onClick={() => handleOpenChange(false)}>Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <LifeBuoy className="h-5 w-5 text-coral" />
                Need help with your creative?
              </DialogTitle>
              <DialogDescription>
                Our team of specialists can adapt this creative to your liking — at no
                additional cost (limited time). Leave your email and we'll take it from there.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label htmlFor="help-email">Email *</Label>
                <Input
                  id="help-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@business.com"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="help-message">What would you like changed? (optional)</Label>
                <Textarea
                  id="help-message"
                  rows={3}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g. swap the music, change the tagline, different pacing…"
                />
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-coral hover:bg-coral-hover text-white border-none">
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    'Request help'
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
